import React, { useState, useContext, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { FormContext } from 'contexts/FormContext';
import { DroneContext } from 'contexts/DroneContext';
import { OrganisationContext } from 'contexts/OrganisationContext';

import { Button, CasaModal, Loader } from 'components/common';
import {
  AuCallout,
  AuFormItemWrapper,
  AuInputLabel
} from 'components/DTA';
import {
  StepContainer,
  Declaration,
  FormNavigation,
  ApplicationName
} from 'components/CASAForm';

import { DroneRegistrationFormData } from 'types/forms/droneRegistrationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { DroneTable } from 'forms/Drones/Common';

const DroneListStep: React.FC = () => {
  const userDetails = useSelector((state: any) => state.userdetails);
  const { openStepById, setFormAlert } = useContext(FormContext);
  const { referenceData, isOrganisationRegistration, contextAlert, setContextAlert, submissionInProgress } = useContext(DroneContext);
  const { organisationDetailsResponse } = useContext(OrganisationContext);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedRemovalDrone, setSelectedRemovalDrone] = useState<number | null>(null);

  const { values, setValues } = useFormikContext<DroneRegistrationFormData>();

  const DRONE_INFO_LIMIT = 95;
  const DRONE_MAX_LIMIT = 100;

  // runs on the first render
  useEffect(() => {
    // Show error message if we have loaded a draft due to an error coming back from Westpac
    if (contextAlert) {
      setFormAlert(contextAlert);
      setContextAlert(null);
    }
  }, []);

  useEffect(() => {
    // Show information alert when number of drones added reaches information message limit
    if (values.drones.length === DRONE_INFO_LIMIT) {
      setFormAlert({
        as: 'info',
        title: 'Drone registration maximum limit almost reached',
        message: `${DRONE_INFO_LIMIT} drones have been added to this application, you cannot add more than ${DRONE_MAX_LIMIT} drones in total`
      });
    }
    if (values.drones.length === DRONE_MAX_LIMIT) {
      setFormAlert({
        as: 'info',
        title: 'Drone registration maximum limit reached',
        message: `Maximum amount of drones allowed to be submitted in one application is ${DRONE_MAX_LIMIT}`
      });
    }
  }, [values.drones.length]);

  // Open add drone screen
  const openDroneEdit = (id?: number) => {
    setFormAlert(null);
    const droneIndex = id ? values.drones.findIndex(drone => drone.id === id) : values.drones.length;
    openStepById('DroneEdit', { droneIndex });
  };

  const removeDrone = (id: number | null) => {
    // Handling edge cases
    if (id == null) {
      return;
    }

    const deletedDrone = values.drones.find(drone => drone.id === id);
    const drones = values.drones.filter(drone => drone != deletedDrone);

    setValues({
      ...values,
      drones: [
        ...drones
      ]
    });

    const alert: IFormAlertType = {
      as: 'success',
      title: 'Drone removed from application'
    };

    setFormAlert(alert);
    setModalVisible(false);
    setSelectedRemovalDrone(null);
    window.scrollTo(0, 0);

    // if all drones have been removed from the form data then send the user back to the begin step
    if (drones.length === 0) {
      openStepById('BeginRegistration');
    }
  };

  // Used to build the name of the drone
  const retrieveDroneName = (id: number | null): string => {
    if (id == null) {
      return '';
    }

    const selectedDrone = values.drones.find(drone => drone.id === id);

    const currentManufacturer = selectedDrone?.manufacturerCode ? referenceData?.value['DRONE_MANUFACTURER'].find(manufacturer => manufacturer.casa_code === selectedDrone.manufacturerCode) : null;
    const currentModel = selectedDrone?.modelCode ? referenceData?.value['DRONE_MODEL'].find(model => model.casa_code === selectedDrone.modelCode) : null;
    const currentCategory = selectedDrone?.categoryCode ? referenceData?.value['DRONE_CATEGORY'].find(category => category.casa_code === selectedDrone.categoryCode) : null;

    return `${selectedDrone?.manufacturer || currentManufacturer?.casa_name} ${selectedDrone?.model || currentModel?.casa_name || currentCategory?.casa_name || ''}`;
  };

  const generateDroneTableData = (intendedUse: string): any => {
    const drones = values.drones;

    return drones.filter(drone => drone.intendedUse === intendedUse).map(drone => {
      const currentModel = drone.modelCode ? referenceData?.value['DRONE_MODEL'].find(model => model.casa_code === drone.modelCode) : null;
      const currentWeightClass = drone.weightClassCode ? referenceData?.value['DRONE_WEIGHT_CLASS'].find(weightClass => weightClass.casa_code === drone.weightClassCode) : null;

      return {
        ...drone,
        name: retrieveDroneName(drone.id),
        weightClass: currentModel && currentModel.droneWeightClass ? currentModel.droneWeightClass : currentWeightClass?.casa_name
      };
    });
  };

  const removeDroneToggle = (id: number) => {
    setModalVisible(true);
    setSelectedRemovalDrone(id);
  };


  if (!referenceData || !referenceData.value) {
    return <Loader message='Working. Please wait.....' />;
  }

  const processTotalFee = () => {
    if (recreationalDroneData.length === 0 && droneData.length === 0) {
      return 0;
    }
    const recreationalTotalFee = recreationalDroneData.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);
    const commercialTotalFee = droneData.reduce((accumulator, current) => accumulator + (current.fee || 0), 0);
    const totalFee = recreationalTotalFee + commercialTotalFee;
    if (droneData.length === 0) {
      recreationalDroneData = [
        ...recreationalDroneData,
        {
          fee: totalFee
        }
      ];
    }
    else {
      droneData = [
        ...droneData,
        {
          fee: totalFee
        }
      ];
    }
    return totalFee;
  };

  // setup data required for display
  let recreationalDroneData = generateDroneTableData('recreational');
  let droneData = generateDroneTableData('commercial');
  const totalFee = processTotalFee();

  return (
    <>
      <ApplicationName name='Drone registration' />
      <StepContainer hidePreviousLink>
        <CasaModal
          heading='Remove drone'
          visible={modalVisible}
          toggle={setModalVisible}
          action={() => removeDrone(selectedRemovalDrone)}
        >
          <p>Are you sure you want to remove your drone?</p>
          <br />
          <AuInputLabel text='Drone' htmlFor='droneName' />
          {retrieveDroneName(selectedRemovalDrone)}

          {selectedRemovalDrone != null && values.drones.find(drone => drone.id === selectedRemovalDrone)?.serialNumber &&
            <>
              <br /><br />
              <AuInputLabel text='Serial number / registration mark' htmlFor='droneName' />
              {values.drones.find(drone => drone.id === selectedRemovalDrone)?.serialNumber}
            </>}
          <br />
          <p>Your drone will not be registered</p>
        </CasaModal>
        <AuCallout title='Your drone has been added to your application.'>
          <p>
            <strong>
              To register your drone, complete the declaration below and submit this form.
            </strong>
          </p>
          <p>
            You can save your progress and return at any time. Use the &apos;save and exit application&apos; button at the bottom of this page.
          </p>
          <p>
            {isOrganisationRegistration
              ? <strong>These drones will be registered to {organisationDetailsResponse?.value?.value[0]?.name?.toUpperCase()}, ARN: {organisationDetailsResponse?.value?.value[0]?.casa_arn}</strong>
              : <strong>These drones will be registered to {userDetails?.items?.value[0]?.fullname?.toUpperCase()}, ARN: {userDetails?.items?.value[0]?.casa_arn}</strong>}
          </p>
        </AuCallout>

        <AuFormItemWrapper>
          <Button as='secondary' onClick={() => openDroneEdit()} disabled={values.drones.length === DRONE_MAX_LIMIT}>Add <abbr title='Remotely piloted aircraft'>drone</abbr></Button>
        </AuFormItemWrapper>

        {recreationalDroneData.length > 0 &&
          <DroneTable
            caption={<>Recreational drones and <abbr title="Remotely piloted aircraft">RPA</abbr></>}
            droneData={recreationalDroneData}
            openDroneEdit={(id: number): void => {
              openDroneEdit(id);
            }}
            removeDroneToggle={(id: number): void => {
              removeDroneToggle(id);
            }} />}
        {droneData.length > 0 &&
          <DroneTable
            caption={<>Commercial drones and <abbr title="Remotely piloted aircraft">RPA</abbr></>}
            droneData={droneData}
            openDroneEdit={(id: number): void => {
              openDroneEdit(id);
            }}
            removeDroneToggle={(id: number): void => {
              removeDroneToggle(id);
            }} />}

        <AuFormItemWrapper>
          <Button as='secondary' onClick={() => openDroneEdit()} disabled={values.drones.length === DRONE_MAX_LIMIT}>Add <abbr title='Remotely piloted aircraft'>drone</abbr></Button>
        </AuFormItemWrapper>

        <Declaration label='I agree to and understand the above conditions'>
          <p>I hereby declare that the particulars set out in this application are true in every respect. I acknowledge that to knowingly make a false or misleading statement in this application is an offence against the Criminal Code Act 1995 (Cth).</p>
          <p>I am aware and accept the risk that information sent electronically may be intercepted and read during transmission, not delivered or modified.</p>
          <p>I have read <a href="https://www.casa.gov.au/node/37026" target="_blank" rel="noopener noreferrer">CASA&apos;s policy on the collection and release of information</a> and I authorise CASA to release information contained on this form in accordance with that policy.</p>
        </Declaration>

        <FormNavigation
          nextButtonText={totalFee > 0 ? 'Proceed to payment' : 'Submit'}
          hidePrevious
          textBlurb={totalFee > 0 ? 'You will be directed to Westpac for payment' : ''}
          saveDraftName='Drone registration'
          triggerFormValidation={true}
          submissionInProgress={submissionInProgress} />
      </StepContainer>
    </>
  );
};

export default DroneListStep;
